
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Briefcase, ExternalLink, ThumbsUp, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface JobListing {
  id: string;
  title: string;
  company: string;
  description?: string;
  url: string;
  published_date?: string;
  keywords?: string[];
  matchScore?: number;
}

interface UserSkill {
  skill_name: string;
  proficiency_level: string;
}

const ITEMS_PER_PAGE = 5;

const JobRecommendationsCard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [allJobListings, setAllJobListings] = useState<JobListing[]>([]);
  const [jobRecommendations, setJobRecommendations] = useState<JobListing[]>([]);
  const [displayedJobs, setDisplayedJobs] = useState<JobListing[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobListing[]>([]);
  const [userSkills, setUserSkills] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterMode, setFilterMode] = useState<string>("matched");
  const [timeFilter, setTimeFilter] = useState<string>("week");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Fetch user skills once when component mounts
  useEffect(() => {
    if (user?.id) {
      fetchUserSkills();
    }
  }, [user?.id]);

  // Fetch job listings when user skills are available
  useEffect(() => {
    if (userSkills.length > 0) {
      fetchJobListings();
    }
  }, [userSkills]);

  // Apply filtering when filter mode or time filter changes
  useEffect(() => {
    applyFilters();
  }, [filterMode, timeFilter, allJobListings, jobRecommendations]);

  // Update pagination when filtered jobs change
  useEffect(() => {
    setTotalPages(Math.ceil(filteredJobs.length / ITEMS_PER_PAGE));
    setCurrentPage(1); // Reset to first page when filters change
    updateDisplayedJobs();
  }, [filteredJobs]);

  // Update displayed jobs when page changes
  useEffect(() => {
    updateDisplayedJobs();
  }, [currentPage]);

  const fetchUserSkills = async () => {
    try {
      const { data, error } = await supabase
        .from('job_seeker_skills')
        .select('skill_name, proficiency_level')
        .eq('job_seeker_id', user.id);

      if (error) throw error;

      if (data) {
        const skills = data
          .filter(skill => skill.skill_name)
          .map(skill => skill.skill_name.toLowerCase());
        
        setUserSkills(skills);
      }
    } catch (error) {
      console.error('Error fetching user skills:', error.message);
    }
  };

  const fetchJobListings = async () => {
    setIsLoading(true);
    try {
      // Calculate cutoff date for 3 months ago
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      
      // Fetch job listings with keywords matching any user skills
      // Also fetch job interests to avoid recommending jobs the user has already shown interest in
      const [listingsResponse, interestsResponse] = await Promise.all([
        supabase
          .from('job_listings')
          .select('*')
          .gte('published_date', threeMonthsAgo.toISOString()) // Only fetch jobs from the last 3 months
          .order('published_date', { ascending: false }),
        
        supabase
          .from('job_interests')
          .select('job_id')
          .eq('user_id', user.id)
      ]);

      if (listingsResponse.error) throw listingsResponse.error;
      if (interestsResponse.error) throw interestsResponse.error;

      const jobListings = listingsResponse.data || [];
      const userInterests = new Set((interestsResponse.data || []).map(interest => interest.job_id));

      // Filter out jobs the user has already shown interest in
      const availableJobs = jobListings.filter(job => !userInterests.has(job.id));

      // Save all filtered jobs
      setAllJobListings(availableJobs);

      // Score jobs based on skill matches
      const scoredJobs = availableJobs.map(job => {
        const keywords = job.keywords || [];
        const normalizedKeywords = keywords.map(k => k.toLowerCase());
        
        // Count how many user skills match with job keywords
        const matchingSkills = userSkills.filter(skill => 
          normalizedKeywords.includes(skill) || 
          normalizedKeywords.some(kw => skill.includes(kw) || kw.includes(skill))
        );
        
        return {
          ...job,
          matchScore: matchingSkills.length,
        };
      });

      // Filter jobs with skill matches and sort by match score
      const recommendations = scoredJobs
        .filter(job => job.matchScore > 0)
        .sort((a, b) => b.matchScore - a.matchScore);

      setJobRecommendations(recommendations);
      
      // Initial filtering will be applied in the useEffect
    } catch (error) {
      console.error('Error fetching job recommendations:', error.message);
      toast({
        title: "Error",
        description: "Failed to load job recommendations. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    // First, select jobs based on filter mode
    const baseJobs = filterMode === "matched" ? jobRecommendations : allJobListings;
    
    // Then apply time filter
    if (timeFilter === "all") {
      setFilteredJobs(baseJobs);
    } else {
      const now = new Date();
      let cutoffDate = new Date();
      
      if (timeFilter === "week") {
        cutoffDate.setDate(now.getDate() - 7); // 1 week ago
      } else if (timeFilter === "month") {
        cutoffDate.setMonth(now.getMonth() - 1); // 1 month ago
      } else if (timeFilter === "three_months") {
        cutoffDate.setMonth(now.getMonth() - 3); // 3 months ago
      }
      
      const timeFilteredJobs = baseJobs.filter(job => {
        if (!job.published_date) return true;
        const publishDate = new Date(job.published_date);
        return publishDate >= cutoffDate;
      });
      
      setFilteredJobs(timeFilteredJobs);
    }
  };

  const updateDisplayedJobs = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedJobs(filteredJobs.slice(startIndex, endIndex));
  };

  const handleExpressInterest = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('job_interests')
        .insert({
          user_id: user.id,
          job_id: jobId
        });

      if (error) throw error;

      // Create notification
      const jobDetails = [...jobRecommendations, ...allJobListings].find(job => job.id === jobId);
      if (jobDetails) {
        await supabase
          .from('notifications')
          .insert({
            user_id: user.id,
            title: "Interest Expressed",
            message: `You expressed interest in ${jobDetails.title} at ${jobDetails.company}`,
            notification_type: "job_interest",
            related_entity_id: jobId
          });
      }

      // Remove job from all lists
      setJobRecommendations(prev => prev.filter(job => job.id !== jobId));
      setAllJobListings(prev => prev.filter(job => job.id !== jobId));
      setFilteredJobs(prev => prev.filter(job => job.id !== jobId));
      
      // Update displayed jobs
      updateDisplayedJobs();

      toast({
        title: "Interest Expressed",
        description: "Job added to your interests.",
      });
    } catch (error) {
      console.error('Error expressing interest:', error.message);
      toast({
        title: "Error",
        description: "Failed to express interest. Please try again.",
        variant: "destructive",
      });
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Hide the card if we have no jobs at all
  if (!isLoading && allJobListings.length === 0 && jobRecommendations.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
            <Briefcase className="h-5 w-5 text-[#0EA5E9]" />
            Job Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-40">
            <p className="text-gray-500">Loading recommendations...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
          <Briefcase className="h-5 w-5 text-[#0EA5E9]" />
          Job Recommendations
        </CardTitle>
        <div className="pt-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <ToggleGroup type="single" value={filterMode} onValueChange={(value) => value && setFilterMode(value)}>
            <ToggleGroupItem value="matched" className="text-sm">
              Skill Matches {jobRecommendations.length > 0 ? `(${jobRecommendations.length})` : ''}
            </ToggleGroupItem>
            <ToggleGroupItem value="all" className="text-sm">
              All Jobs
            </ToggleGroupItem>
          </ToggleGroup>
          
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-gray-500" />
            <Select 
              value={timeFilter} 
              onValueChange={setTimeFilter}
            >
              <SelectTrigger className="h-8 w-auto min-w-[120px]">
                <SelectValue placeholder="Time filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="week">Past week</SelectItem>
                <SelectItem value="month">Past month</SelectItem>
                <SelectItem value="three_months">Past 3 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {filteredJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40">
            <p className="text-gray-500 text-center">
              {filterMode === "matched" 
                ? (userSkills.length > 0 
                  ? "No job matches found with current filters. Try changing the filters!" 
                  : "Add skills to your profile to get job matches")
                : "No job listings available with current filters. Try changing the filters!"}
            </p>
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-500">
              Showing {displayedJobs.length} of {filteredJobs.length} jobs
            </div>
            
            {displayedJobs.map((job) => (
              <div key={job.id} className="p-4 bg-white border border-[#0EA5E9]/20 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-800">{job.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{job.company}</p>
                    {job.published_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Posted: {new Date(job.published_date).toLocaleDateString()}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {job.keywords?.slice(0, 3).map((keyword, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-blue-50">
                          {keyword}
                        </Badge>
                      ))}
                      {job.matchScore && job.matchScore > 0 && (
                        <Badge className="text-xs bg-green-100 text-green-800">
                          {job.matchScore} Skill Match{job.matchScore !== 1 ? 'es' : ''}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-[#0EA5E9] border-[#0EA5E9]/20 hover:bg-[#0EA5E9]/10"
                      onClick={() => window.open(job.url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm"
                      className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90"
                      onClick={() => handleExpressInterest(job.id)}
                    >
                      <ThumbsUp className="h-4 w-4 mr-1" /> Interest
                    </Button>
                  </div>
                </div>
                {job.description && (
                  <div 
                    className="text-sm text-gray-600 mt-2 line-clamp-2 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: job.description }}
                  />
                )}
              </div>
            ))}
            
            {/* Pagination controls */}
            {filteredJobs.length > ITEMS_PER_PAGE && (
              <div className="flex justify-between items-center">
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={prevPage}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={nextPage}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
        <div className="flex justify-center mt-2">
          <Button 
            variant="outline" 
            className="text-[#0EA5E9] border-[#0EA5E9]/20 hover:bg-[#0EA5E9]/10"
            onClick={fetchJobListings}
          >
            Refresh Jobs
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default JobRecommendationsCard;
